/**
 * StoreIntegration.ts (AbstractLicensingBackend)
 *
 * Copyright © 2026 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rogiel Sulzbach, 2026
 *
 * @file StoreIntegration.ts
 * @author Rogiel Sulzbach
 * @copyright 2026 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { BASE_API_URL } from '../config';
import { IAPIEntityResponse } from '@abstract/abstractwebcommon-shared/interfaces/api';
import { HttpClient } from '@abstract/abstractwebcommon-client/utils/HttpClient';
import { createLogApi } from './Log';

const httpClient = new HttpClient(BASE_API_URL, createLogApi);

/**
 * The sentinel submitted for an untouched API key.
 *
 * An empty string cannot mean "leave it alone", because it has to mean "clear it" — otherwise a
 * deployment could never be disconnected from the Store. So an untouched field submits this instead
 * and the server preserves what it already holds.
 */
export const UNCHANGED_API_KEY = 'unchanged';

/** The Store configuration, as the admin API reports it. Never includes the API key itself. */
export interface IStoreConfiguration {
  storeBaseURL: string;
  hasAPIKey: boolean;
  storeReconcileIntervalSeconds: number;
  storeWebhookLogRetentionDays: number;
}

/** Everything an operator needs to answer "is this working". */
export interface IStoreStatus {
  isConfigured: boolean;
  replicaAgeMs: number | null;
  webhookPath: string;
  entitlementCounts: Record<string, number>;
  webhookOutcomesLast24h: Record<string, number>;
}

export interface IStoreConfigurationResponse {
  configuration: IStoreConfiguration;
  status: IStoreStatus;
}

/** One line of the inbound delivery audit trail. */
export interface IStoreWebhookLog {
  id: string;
  receivedAt: string;
  outcome: string;
  eventID: string | null;
  eventType: string | null;
  userUUID: string | null;
  entitlementKey: string | null;
  entitlementVersion: number | null;
  sourceIP: string | null;
  detail: string | null;
}

export interface IStoreWebhookLogResponse {
  total: number;
  logs: IStoreWebhookLog[];
}

/** What the Store said when asked to prove the credentials work. */
export interface IStoreVerifyResponse {
  ok: boolean;
  entitlementKeys: string[];
  error: string | null;
}

/** Reads the Store configuration and status. */
export const getStoreConfiguration = async (): Promise<
  IAPIEntityResponse<IStoreConfigurationResponse>
> => httpClient.get<IStoreConfigurationResponse>('/store/configuration');

/**
 * Writes the Store configuration.
 *
 * Every field is optional so one control can be saved without round-tripping the others.
 */
export const updateStoreConfiguration = async (
  configuration: Partial<{
    storeBaseURL: string;
    storeAPIKey: string;
    storeReconcileIntervalSeconds: number;
    storeWebhookLogRetentionDays: number;
  }>
): Promise<IAPIEntityResponse<IStoreConfigurationResponse>> =>
  httpClient.post<IStoreConfigurationResponse>(
    '/store/configuration',
    configuration
  );

/** Confirms the credentials work and returns the entitlement keys this deployment is scoped to. */
export const verifyStoreCredentials = async (): Promise<
  IAPIEntityResponse<IStoreVerifyResponse>
> => httpClient.post<IStoreVerifyResponse>('/store/verify', {});

/** Reads the inbound delivery audit trail. */
export const getStoreWebhookLogs = async (
  outcome?: string
): Promise<IAPIEntityResponse<IStoreWebhookLogResponse>> =>
  httpClient.get<IStoreWebhookLogResponse>(
    `/store/logs?limit=50${outcome ? `&outcome=${encodeURIComponent(outcome)}` : ''}`
  );

/** Runs a reconciliation pass and a license-projection repair now. */
export const reconcileStoreNow = async (): Promise<IAPIEntityResponse<any>> =>
  httpClient.post<any>('/store/reconcile', {});
